/* tslint:disable:object-literal-sort-keys */

import { ntc } from './color-ntc.utils';
// @dynamic
export class ColorUtils {
  public static COLOR_SPACES = {
    HSV: 'HSV',
    K: 'K',
    RGB: 'RGB'
  };

  public static RGBtoHEX = (r: number, g: number, b: number) => {
    const componentToHex = (n: number) => {
      const hex = Math.trunc(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  };

  public static HEXtoRGB = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  };

  public static HSVtoRGB = (h: number, s: number, v: number) => {
    let r = 0,
      g = 0,
      b = 0,
      i,
      f,
      p,
      q,
      t;
    h /= 360;
    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);
    switch (i % 6) {
      case 0:
        (r = v), (g = t), (b = p);
        break;
      case 1:
        (r = q), (g = v), (b = p);
        break;
      case 2:
        (r = p), (g = v), (b = t);
        break;
      case 3:
        (r = p), (g = q), (b = v);
        break;
      case 4:
        (r = t), (g = p), (b = v);
        break;
      case 5:
        (r = v), (g = p), (b = q);
        break;
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  };

  public static RGBtoHSV = (r: number, g: number, b: number) => {
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b),
      d = max - min,
      s = max === 0 ? 0 : d / max,
      v = max / 255;
    let h = 0;

    switch (max) {
      case min:
        h = 0;
        break;
      case r:
        h = g - b + d * (g < b ? 6 : 0);
        h /= 6 * d;
        break;
      case g:
        h = b - r + d * 2;
        h /= 6 * d;
        break;
      case b:
        h = r - g + d * 4;
        h /= 6 * d;
        break;
    }

    h *= 360;

    return { h, s, v };
  };

  public static xyYtoxyZ = (x: number, y1: number, y2: number) => {
    return {
      x: x * (y1 / y2),
      y: y1,
      z: (1 - x - y2) * (y1 / y2)
    };
  };

  public static xyZtoRGB = (x: number, y: number, z: number) => {
    const toRGB = (n: number) => {
      const result = 255 * n;
      if (result < 0) {
        return 0;
      }
      if (result > 255) {
        return 255;
      }
      return result;
    };

    x = x / 100;
    y = y / 100;
    z = z / 100;

    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let b = x * 0.0557 + y * -0.204 + z * 1.057;

    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;

    return {
      r: toRGB(r),
      g: toRGB(g),
      b: toRGB(b)
    };
  };

  public static RGBtoxyZ = (r: number, g: number, b: number) => {
    const pivotRGB = (n: number) => {
      return (
        (n > 0.04045 ? Math.pow((n + 0.055) / 1.055, 2.4) : n / 12.92) * 100.0
      );
    };

    r = pivotRGB(r / 255);
    g = pivotRGB(g / 255);
    b = pivotRGB(b / 255);

    return {
      x: r * 0.4124 + g * 0.3576 + b * 0.1805,
      y: r * 0.2126 + g * 0.7152 + b * 0.0722,
      z: r * 0.0193 + g * 0.1192 + b * 0.9505
    };
  };

  public static xyYtoRGB = (x: number, y1: number, y2: number) => {
    const { x: x1, y, z } = ColorUtils.xyYtoxyZ(x, y1, y2);
    return ColorUtils.xyZtoRGB(x1, y, z);
  };

  public static RGBtoxyY = (r: number, g: number, b: number) => {
    const { x, y, z } = ColorUtils.RGBtoxyZ(r, g, b);
    return {
      x: x + y + z <= 0.01 ? 0 : x / (x + y + z),
      y1: y,
      y2: x + y + z <= 0.01 ? 0 : y / (x + y + z)
    };
  };

    public static KtoColor = (k: number) => {
        const temperature = k / 100.0;
        if (temperature < 2700) {
          return 'warm white';
        } else if (temperature >= 2700 && temperature < 4000) {
          return 'soft white';
        } else if (temperature >= 4000 && temperature < 5500) {
          return 'white';
        } else if (temperature >= 5500 && temperature < 7000) {
          return 'daylight white';
        } else if (temperature >= 7000) {
          return 'cool white';
        }

        return 'none';
    };

    public static KtoRGB = (k: number) => {
    const temperature = k / 100.0;
    let red, green, blue;

    /* Calculate red */
    if (temperature < 66.0) {
      red = 255;
    } else {
      red = temperature - 55.0;
      red =
        351.97690566805693 +
        0.114206453784165 * red -
        40.25366309332127 * Math.log(red);
      if (red < 0) {
        red = 0;
      }
      if (red > 255) {
        red = 255;
      }
    }

    /* Calculate green */
    if (temperature < 66.0) {
      green = temperature - 2;
      green =
        -155.25485562709179 -
        0.44596950469579133 * green +
        104.49216199393888 * Math.log(green);
      if (green < 0) {
        green = 0;
      }
      if (green > 255) {
        green = 255;
      }
    } else {
      green = temperature - 50.0;
      green =
        325.4494125711974 +
        0.07943456536662342 * green -
        28.0852963507957 * Math.log(green);
      if (green < 0) {
        green = 0;
      }
      if (green > 255) {
        green = 255;
      }
    }

    /* Calculate blue */
    if (temperature >= 66.0) {
      blue = 255;
    } else {
      if (temperature <= 20.0) {
        blue = 0;
      } else {
        blue = temperature - 10;
        blue =
          -254.76935184120902 +
          0.8274096064007395 * blue +
          115.67994401066147 * Math.log(blue);
        if (blue < 0) {
          blue = 0;
        }
        if (blue > 255) {
          blue = 255;
        }
      }
    }

    return {
      r: Math.round(red),
      g: Math.round(green),
      b: Math.round(blue)
    };
  };

  public static RGBtoK = (r: number, g: number, b: number) => {
    let temperature = 0,
      testRGB;
    const epsilon = 0.4;
    let minTemperature = 1000;
    let maxTemperature = 40000;

    while (maxTemperature - minTemperature > epsilon) {
      temperature = (maxTemperature + minTemperature) / 2;
      testRGB = ColorUtils.KtoRGB(temperature);
      if (testRGB.b / testRGB.r >= b / r) {
        maxTemperature = temperature;
      } else {
        minTemperature = temperature;
      }
    }

    return Math.round(temperature);
  };

  public static RGBtoColorName = (rgb: any) => {
    return ColorUtils.HEXtoColorName(ColorUtils.RGBtoHEX(rgb.r, rgb.g, rgb.b));
  };

  public static HEXtoColorName = (color: string) => {
    const match = ntc.name(color);
    return match && match.length >= 2 ? match[1] : { en: color, es: color };
  };

  public static closestColor = (arr: string[], str: string) => {
    const dist = (s: string, t: string): number => {
      if (!s.length || !t.length) {
        return 0;
      }
      return (
        dist(s.slice(2), t.slice(2)) +
        Math.abs(parseInt(s.slice(0, 2), 16) - parseInt(t.slice(0, 2), 16))
      );
    };

    let min = 0xffffff;
    let best, current, i;
    for (i = 0; i < arr.length; i++) {
      current = dist(arr[i], str);
      if (current < min) {
        min = current;
        best = arr[i];
      }
    }

    return best;
  };
}
